import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useGetOne, useRecordContext } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";

export type ProjectUnitAmountContextValue = {
  unitAmount: number | undefined;
  isLoading: boolean;
  projectId: string | undefined;
  markCommittedManuallyEdited: () => void;
};

const ProjectUnitAmountContext = createContext<ProjectUnitAmountContextValue>({
  unitAmount: undefined,
  isLoading: false,
  projectId: undefined,
  markCommittedManuallyEdited: () => {},
});

export function useProjectUnitAmount(): ProjectUnitAmountContextValue {
  return useContext(ProjectUnitAmountContext);
}

type ProjectUnitAmountProviderProps = {
  routeProjectId?: string | null;
  skipInitialCommittedSeed?: boolean;
  children: ReactNode;
};

function calculatedCommitted(units: unknown, unitAmount: number | undefined): number | undefined {
  if (unitAmount == null) return undefined;
  const u = Number(units);
  if (!Number.isFinite(u) || u < 0) return undefined;
  return Math.round(u * unitAmount);
}

function SeedCommittedFromUnits({
  skipInitialSeed,
  committedManuallyEditedRef,
}: {
  skipInitialSeed: boolean;
  committedManuallyEditedRef: React.MutableRefObject<boolean>;
}) {
  const { unitAmount } = useProjectUnitAmount();
  const { control, setValue, getValues } = useFormContext();
  const units = useWatch({ control, name: "units" });
  const skippedInitial = useRef(!skipInitialSeed);
  const markedEditOnLoad = useRef(false);

  useEffect(() => {
    if (skipInitialSeed && !markedEditOnLoad.current && unitAmount != null) {
      const calc = calculatedCommitted(getValues("units"), unitAmount);
      const committed = Number(getValues("committedAmount"));
      if (calc != null && Number.isFinite(committed) && committed !== calc) {
        committedManuallyEditedRef.current = true;
      }
      markedEditOnLoad.current = true;
    }
  }, [skipInitialSeed, unitAmount, getValues, committedManuallyEditedRef]);

  useEffect(() => {
    if (skipInitialSeed && !skippedInitial.current) {
      skippedInitial.current = true;
      return;
    }
    skippedInitial.current = true;

    if (committedManuallyEditedRef.current) return;

    const next = calculatedCommitted(units, unitAmount);
    if (next == null) return;

    setValue("committedAmount", next, { shouldDirty: false, shouldTouch: false });
  }, [units, unitAmount, setValue, skipInitialSeed, committedManuallyEditedRef]);

  return null;
}

/** Loads `projects.unitAmount` and seeds `committedAmount` from units × unit amount. */
export function ProjectUnitAmountProvider({
  routeProjectId,
  skipInitialCommittedSeed = false,
  children,
}: ProjectUnitAmountProviderProps) {
  const record = useRecordContext();
  const { control } = useFormContext();
  const formProjectId = useWatch({ control, name: "projectId" });
  const committedManuallyEditedRef = useRef(false);

  const projectId = useMemo(() => {
    const fromRoute = routeProjectId?.trim() || "";
    const fromRecord =
      record?.projectId != null && record.projectId !== "" ? String(record.projectId).trim() : "";
    const fromForm =
      formProjectId != null && formProjectId !== "" ? String(formProjectId).trim() : "";
    return fromRoute || fromRecord || fromForm || undefined;
  }, [routeProjectId, record?.projectId, formProjectId]);

  const { data, isLoading } = useGetOne(
    "projects",
    { id: projectId! },
    { enabled: Boolean(projectId) }
  );

  const unitAmount =
    data?.unitAmount != null && Number.isFinite(Number(data.unitAmount))
      ? Number(data.unitAmount)
      : undefined;

  const markCommittedManuallyEdited = useCallback(() => {
    committedManuallyEditedRef.current = true;
  }, []);

  const value = useMemo(
    () => ({
      unitAmount,
      isLoading,
      projectId,
      markCommittedManuallyEdited,
    }),
    [unitAmount, isLoading, projectId, markCommittedManuallyEdited]
  );

  return (
    <ProjectUnitAmountContext.Provider value={value}>
      <SeedCommittedFromUnits
        skipInitialSeed={skipInitialCommittedSeed}
        committedManuallyEditedRef={committedManuallyEditedRef}
      />
      {children}
    </ProjectUnitAmountContext.Provider>
  );
}
